/**
 * SignatureFicheProgresPublic.jsx
 * Page publique (smartphone) — le formateur signe sa fiche de progrès avec le doigt (le tracé à la souris est de mauvaise qualité).
 * Accessible via /signature-fiche-progres/:token (QR code affiché dans la fiche de progrès, valable 20 minutes).
 */
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { SignatureCanvas } from '../components/Shared';

const API = process.env.REACT_APP_API_URL || `${window.location.origin}/api`;

const page = { minHeight: '100vh', background: '#f1f5f9', padding: '16px 12px', fontFamily: 'Arial, Helvetica, sans-serif', color: '#1e293b', boxSizing: 'border-box' };
const carte = { maxWidth: 620, margin: '0 auto', background: '#fff', borderRadius: 14, padding: '20px 18px', boxShadow: '0 4px 20px rgba(0,0,0,.08)' };
const bouton = (fond = '#1a3668') => ({ width: '100%', padding: '14px', borderRadius: 10, border: 'none', background: fond, color: '#fff', fontWeight: 700, fontSize: 16, cursor: 'pointer' });

async function appeler(chemin, corps) {
  const r = await fetch(`${API}/signature-fiche-progres-public/${chemin}`, corps
    ? { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(corps) }
    : undefined);
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(data.error || 'Erreur'), { status: r.status });
  return data;
}

function Enveloppe({ children }) {
  return (
    <div style={page}>
      <div style={carte}>
        <div style={{ textAlign: 'center', borderBottom: '3px solid #E87722', paddingBottom: 10, marginBottom: 16, fontWeight: 700, fontSize: 18, color: '#E87722' }}>CEJ Concept</div>
        {children}
      </div>
    </div>
  );
}

export default function SignatureFicheProgresPublic() {
  const { token } = useParams();
  const [etape, setEtape] = useState('chargement'); // chargement | erreur | ordinateur | signature | apercu | fini
  const [info, setInfo] = useState(null);
  const [erreur, setErreur] = useState('');
  const [image, setImage] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [portrait, setPortrait] = useState(window.innerHeight > window.innerWidth);

  useEffect(() => {
    const maj = () => setPortrait(window.innerHeight > window.innerWidth);
    window.addEventListener('resize', maj);
    return () => window.removeEventListener('resize', maj);
  }, []);

  useEffect(() => {
    appeler(token)
      .then(d => {
        setInfo(d);
        // Ouvert depuis un ordinateur (sans écran tactile) : la signature se fait au smartphone
        setEtape((navigator.maxTouchPoints || 0) > 0 ? 'signature' : 'ordinateur');
      })
      .catch(e => { setErreur(e.message); setEtape('erreur'); });
  }, [token]);

  async function enregistrer() {
    setEnvoi(true); setErreur('');
    try {
      await appeler(`${token}/signer`, { image });
      setEtape('fini');
    } catch (e) {
      setErreur(e.message);
      setEtape(e.status === 422 ? 'signature' : e.status === 409 || e.status === 410 || e.status === 404 ? 'erreur' : 'apercu');
    } finally { setEnvoi(false); }
  }

  if (etape === 'chargement') return <Enveloppe><p style={{ textAlign: 'center', color: '#64748b' }}>⏳ Chargement…</p></Enveloppe>;

  if (etape === 'erreur') return (
    <Enveloppe>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 44 }}>🔒</div>
        <h2 style={{ margin: '8px 0' }}>Lien non disponible</h2>
        <p style={{ color: '#475569', fontSize: 15 }}>{erreur}</p>
      </div>
    </Enveloppe>
  );

  if (etape === 'ordinateur') return (
    <Enveloppe>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 44 }}>📱</div>
        <h2 style={{ margin: '8px 0' }}>Ouvrez ce lien sur votre smartphone</h2>
        <p style={{ color: '#475569', fontSize: 15, lineHeight: 1.5 }}>
          Votre signature doit être tracée avec le doigt sur l'écran d'un smartphone, pour être de qualité.
          Scannez le QR code affiché dans la fiche de progrès avec l'appareil photo de votre téléphone.
        </p>
      </div>
    </Enveloppe>
  );

  if (etape === 'signature') return (
    <Enveloppe>
      <h2 style={{ fontSize: 17, margin: '0 0 4px' }}>Fiche de progrès{info?.nom ? ` — ${info.nom}` : ''}</h2>
      <p style={{ color: '#475569', fontSize: 14, margin: '0 0 12px' }}>
        {info?.formation}{info?.session_id ? ` – ${info.session_id}` : ''}
      </p>
      <div style={{ background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 10, padding: 12, margin: '0 0 12px', fontSize: 14, lineHeight: 1.5 }}>
        <div style={{ fontWeight: 700, color: '#c2410c', marginBottom: 4 }}>⚠ Signez en grand, avec le doigt</div>
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          <li>Occupez la majeure partie du cadre</li>
          <li>Tracez d'un geste continu, sans lever le doigt trop souvent</li>
        </ul>
      </div>
      {portrait && (
        <div style={{ background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 10, padding: 10, marginBottom: 12, fontSize: 14, fontWeight: 600 }}>
          📱 Tournez votre téléphone en travers (mode paysage) pour disposer d'un cadre plus large.
        </div>
      )}
      {erreur && <div role="alert" style={{ color: '#b91c1c', fontWeight: 600, marginBottom: 10 }}>{erreur}</div>}
      <SignatureCanvas controleQualite hauteur={portrait ? 200 : 170} labelValider="Voir l'aperçu"
        onSave={img => { setImage(img); setErreur(''); setEtape('apercu'); }} />
    </Enveloppe>
  );

  if (etape === 'apercu') return (
    <Enveloppe>
      <h2 style={{ margin: '0 0 6px', fontSize: 18 }}>Votre signature</h2>
      <p style={{ color: '#475569', fontSize: 14, marginTop: 0 }}>Elle sera apposée sur votre fiche de progrès. Elle vous convient ?</p>
      <div style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 10, padding: 16, textAlign: 'center', marginBottom: 16 }}>
        <img src={image} alt="Aperçu de votre signature" style={{ maxWidth: '100%', maxHeight: 160, objectFit: 'contain' }} />
      </div>
      {erreur && <div role="alert" style={{ color: '#b91c1c', fontWeight: 600, marginBottom: 10 }}>{erreur}</div>}
      <div style={{ display: 'flex', gap: 10 }}>
        <button type="button" onClick={() => { setImage(null); setErreur(''); setEtape('signature'); }} style={{ ...bouton('#fff'), color: '#475569', border: '1px solid #cbd5e1', flex: 1 }}>Recommencer</button>
        <button type="button" onClick={enregistrer} disabled={envoi} style={{ ...bouton('#16a34a'), flex: 2, opacity: envoi ? 0.6 : 1 }}>{envoi ? '⏳ Enregistrement…' : '✓ Valider ma signature'}</button>
      </div>
    </Enveloppe>
  );

  return (
    <Enveloppe>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 48 }}>✅</div>
        <h2 style={{ margin: '8px 0' }}>Signature enregistrée</h2>
        <p style={{ color: '#475569', fontSize: 15, lineHeight: 1.5 }}>
          Merci{info?.nom ? ` ${info.nom}` : ''}. Votre signature apparaît maintenant sur la fiche de progrès.
        </p>
        <p style={{ color: '#64748b', fontSize: 13 }}>Revenez à l'ordinateur pour terminer et soumettre la fiche. Vous pouvez fermer cette page.</p>
      </div>
    </Enveloppe>
  );
}
